<template>
  <modal-vue
    :modal-active="modelValue"
    max-width="max-w-[678px]"
    @close="$emit('update:modelValue', false)"
  >
    <template #header>
      <h2 class="text-lg font-bold text-[#16161d] text-left">Candidatos para o convite de troca</h2>
    </template>

    <div class="flex flex-col gap-4 text-left">
      <div v-if="directInvites.length" class="flex flex-col">
        <div class="flex items-center justify-between mb-3.5">
          <span class="text-base font-bold text-primary30">Convidados diretos</span>
          <span class="text-sm text-[#52526f]">
            {{ directInvites.length }} {{ directInvites.length === 1 ? 'convidado' : 'convidados' }}
          </span>
        </div>

        <template v-for="(invite, index) in directInvites" :key="invite.id">
          <div class="flex items-start gap-3">
            <div
              class="relative shrink-0"
              :style="{ width: SVG_SIZE + 'px', height: SVG_SIZE + 'px' }"
            >
              <svg
                :width="SVG_SIZE"
                :height="SVG_SIZE"
                class="absolute top-0 left-0 -rotate-90"
                aria-hidden="true"
              >
                <circle
                  :cx="SVG_SIZE / 2"
                  :cy="SVG_SIZE / 2"
                  :r="RADIUS"
                  :stroke="NEUTRAL_RING_COLOR"
                  :stroke-width="STROKE_WIDTH"
                  fill="none"
                />
                <circle
                  v-if="getClampedScore(invite.doctor.npsScore) > 0"
                  :cx="SVG_SIZE / 2"
                  :cy="SVG_SIZE / 2"
                  :r="RADIUS"
                  :stroke="getArcColor(invite.doctor.npsScore)"
                  :stroke-width="STROKE_WIDTH"
                  fill="none"
                  :stroke-dasharray="CIRCUMFERENCE"
                  :stroke-dashoffset="getArcOffset(invite.doctor.npsScore)"
                  stroke-linecap="round"
                />
              </svg>
              <div
                class="absolute rounded-full flex items-center justify-center text-sm font-semibold text-white"
                :style="{ ...INNER_STYLE, backgroundColor: getAvatarColor(invite.doctor.name) }"
              >
                {{ getDoctorInitials(invite.doctor.name) }}
              </div>
            </div>
            <div class="flex-1 flex flex-col gap-0.5 min-w-0 overflow-hidden">
              <span class="text-sm font-semibold text-[#222]">{{
                getFirstAndSecondName(invite.doctor.name)
              }}</span>
              <span class="text-xs text-[#52526f]">CRM {{ invite.doctor.crm }}</span>
              <span v-if="invite.acceptedAt" class="text-xs text-[#52526f]">
                Aceitou em {{ formatAcceptedAt(invite.acceptedAt) }}
              </span>
              <span
                v-if="getExpirationLabel(invite)"
                class="text-xs font-medium text-[#a15c07]"
                data-testid="candidate-expiration"
              >
                {{ getExpirationLabel(invite) }}
              </span>
              <span v-if="invite.declineReason" class="text-xs text-[#999] italic">
                Motivo: {{ invite.declineReason }}
              </span>
            </div>
            <div class="shrink-0">
              <span
                class="inline-block px-2 py-[3px] rounded-[10px] text-[11px] font-medium whitespace-nowrap"
                :class="getStatusBadgeClasses(invite)"
              >
                {{ getStatusLabel(invite) }}
              </span>
            </div>
          </div>

          <div v-if="index < directInvites.length - 1" class="h-3.5" />
          <div v-if="index < directInvites.length - 1" class="h-px bg-[#e0e0e0]" />
          <div v-if="index < directInvites.length - 1" class="h-3.5" />
        </template>
      </div>

      <div v-if="showcaseInvites.length" class="flex flex-col">
        <div class="flex items-center justify-between mb-3.5">
          <span class="text-base font-bold text-primary30">Candidatos da vitrine</span>
          <span class="text-sm text-[#52526f]">
            {{ showcaseInvites.length }}
            {{ showcaseInvites.length === 1 ? 'candidato' : 'candidatos' }}
          </span>
        </div>

        <template v-for="(invite, index) in showcaseInvites" :key="invite.id">
          <div class="flex items-start gap-3">
            <div
              class="relative shrink-0"
              :style="{ width: SVG_SIZE + 'px', height: SVG_SIZE + 'px' }"
            >
              <svg
                :width="SVG_SIZE"
                :height="SVG_SIZE"
                class="absolute top-0 left-0 -rotate-90"
                aria-hidden="true"
              >
                <circle
                  :cx="SVG_SIZE / 2"
                  :cy="SVG_SIZE / 2"
                  :r="RADIUS"
                  :stroke="NEUTRAL_RING_COLOR"
                  :stroke-width="STROKE_WIDTH"
                  fill="none"
                />
                <circle
                  v-if="getClampedScore(invite.doctor.npsScore) > 0"
                  :cx="SVG_SIZE / 2"
                  :cy="SVG_SIZE / 2"
                  :r="RADIUS"
                  :stroke="getArcColor(invite.doctor.npsScore)"
                  :stroke-width="STROKE_WIDTH"
                  fill="none"
                  :stroke-dasharray="CIRCUMFERENCE"
                  :stroke-dashoffset="getArcOffset(invite.doctor.npsScore)"
                  stroke-linecap="round"
                />
              </svg>
              <div
                class="absolute rounded-full flex items-center justify-center text-sm font-semibold text-white"
                :style="{ ...INNER_STYLE, backgroundColor: getAvatarColor(invite.doctor.name) }"
              >
                {{ getDoctorInitials(invite.doctor.name) }}
              </div>
            </div>
            <div class="flex-1 flex flex-col gap-0.5 min-w-0 overflow-hidden">
              <span class="text-sm font-semibold text-[#222]">{{
                getFirstAndSecondName(invite.doctor.name)
              }}</span>
              <span class="text-xs text-[#52526f]">CRM {{ invite.doctor.crm }}</span>
              <span v-if="invite.acceptedAt" class="text-xs text-[#52526f]">
                Aceitou em {{ formatAcceptedAt(invite.acceptedAt) }}
              </span>
              <span
                v-if="getExpirationLabel(invite)"
                class="text-xs font-medium text-[#a15c07]"
                data-testid="candidate-expiration"
              >
                {{ getExpirationLabel(invite) }}
              </span>
              <span v-if="invite.declineReason" class="text-xs text-[#999] italic">
                Motivo: {{ invite.declineReason }}
              </span>
            </div>
            <div class="shrink-0">
              <span
                class="inline-block px-2 py-[3px] rounded-[10px] text-[11px] font-medium whitespace-nowrap"
                :class="getStatusBadgeClasses(invite)"
              >
                {{ getStatusLabel(invite) }}
              </span>
            </div>
          </div>

          <div v-if="index < showcaseInvites.length - 1" class="h-3.5" />
          <div v-if="index < showcaseInvites.length - 1" class="h-px bg-[#e0e0e0]" />
          <div v-if="index < showcaseInvites.length - 1" class="h-3.5" />
        </template>
      </div>

      <FilledButton
        text="Fechar"
        width="100%"
        height="48px"
        class="bg-primary30"
        @click="$emit('update:modelValue', false)"
      />
    </div>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { differenceInDays, differenceInHours, format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { InviteResponseV2 } from '@/Network/Types/Responses/HandoverInviteResponses'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import { getDoctorInitials } from '@/utils/string'
import { InviteStatus, InviteOrigin } from '@/Network/Types/Requests/HandoverInviteRequests'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'

const AVATAR_SIZE = 40
const STROKE_WIDTH = 4
const RADIUS = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const SVG_SIZE = RADIUS * 2 + STROKE_WIDTH
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const INNER_STYLE = {
  width: AVATAR_SIZE + 'px',
  height: AVATAR_SIZE + 'px',
  top: STROKE_WIDTH + 'px',
  left: STROKE_WIDTH + 'px'
}

function getClampedScore(score?: number | null): number {
  if (score == null) return 0
  return Math.min(100, Math.max(0, score))
}

function getArcOffset(score?: number | null): number {
  return CIRCUMFERENCE * (1 - getClampedScore(score) / 100)
}

function getArcColor(score?: number | null): string {
  if (score == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(score).color
}

const props = defineProps<{
  modelValue: boolean
  invites: InviteResponseV2[]
}>()

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const directInvites = computed(() => props.invites.filter((i) => i.origin === InviteOrigin.DIRECT))

const showcaseInvites = computed(() =>
  props.invites.filter((i) => i.origin === InviteOrigin.SHOWCASE)
)

const acceptedDirectQueue = computed(() =>
  directInvites.value
    .filter((i) => i.status === InviteStatus.ACCEPTED)
    .sort((a, b) => {
      if (!a.acceptedAt || !b.acceptedAt) return 0
      return new Date(a.acceptedAt).getTime() - new Date(b.acceptedAt).getTime()
    })
)

function getQueuePosition(invite: InviteResponseV2): number {
  return acceptedDirectQueue.value.findIndex((i) => i.id === invite.id) + 1
}

const AVATAR_COLORS = [
  '#4778c7',
  '#804db2',
  '#995933',
  '#2e7d32',
  '#c62828',
  '#00838f',
  '#ad1457',
  '#4527a0',
  '#ef6c00',
  '#1565c0'
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function formatAcceptedAt(dateStr: string): string {
  try {
    const date = parseISO(dateStr)
    return format(date, "EEE dd/MM 'às' HH'h'mm", { locale: ptBR })
  } catch {
    return dateStr
  }
}

/**
 * Prazo restante do convite, só para quem ainda pode responder. Um convite já
 * respondido ou já vencido tem o próprio status contando a história — repetir o
 * prazo ali só competiria com ele.
 */
function getExpirationLabel(invite: InviteResponseV2): string | null {
  if (invite.status !== InviteStatus.PENDING || !invite.expiresAt) return null

  try {
    const expiresAt = parseISO(invite.expiresAt)
    const hours = differenceInHours(expiresAt, new Date())
    if (hours < 0) return null
    if (hours < 24) return `Expira em ${hours} ${hours === 1 ? 'hora' : 'horas'}`

    const days = differenceInDays(expiresAt, new Date())
    return `Expira em ${days} ${days === 1 ? 'dia' : 'dias'}`
  } catch {
    return null
  }
}

function getStatusLabel(invite: InviteResponseV2): string {
  if (invite.status === InviteStatus.ACCEPTED) {
    const pos = getQueuePosition(invite)
    return pos > 0 ? `${pos}º na fila` : 'Aceito'
  }
  if (invite.status === InviteStatus.PENDING) return 'Aguardando'
  if (invite.status === InviteStatus.DECLINED) return 'Recusou'
  if (invite.status === InviteStatus.EXPIRED) return 'Expirado'
  if (invite.status === InviteStatus.CANCELLED) return 'Cancelado'
  return invite.status
}

function getStatusBadgeClasses(invite: InviteResponseV2): string {
  if (invite.status === InviteStatus.ACCEPTED) {
    return 'bg-primary5 text-[#235e6c]'
  }
  return 'bg-[#e2e2e9] text-[#999]'
}
</script>

<style scoped>
:deep(header) {
  padding: 16px 16px 0 16px !important;
}

:deep(main) {
  padding: 16px !important;
}
</style>
